import React, { useState, useRef, useEffect, startTransition } from 'react';
import { Play, MoreVertical, ListMusic, PlusSquare, Share2, Radio, Library, Album, User, Info, ThumbsDown, X } from 'lucide-react';
import { usePlayerActions } from '../context/PlayerContext';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE } from '../services/api';

function SongListRow({ song, playlist = [] }) {
  const { playSong } = usePlayerActions();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePlay = (e) => {
    e.preventDefault();
    e.stopPropagation();
    playSong(song, playlist);
  };

  const handleAction = (type) => {
    setShowMenu(false);
    switch(type) {
      case 'artist':
        if (song.artist?.id) navigate(`/artists/${song.artist.id}`);
        break;
      case 'share':
        if (navigator.share) {
          navigator.share({ 
            title: song.title, 
            text: `Check out ${song.title} by ${song.artist?.name} on Hariwood!`,
            url: window.location.origin + `/song/${song.id}` 
          }).catch(err => console.log('Share failed', err));
        } else {
          navigator.clipboard.writeText(window.location.origin + `/song/${song.id}`);
          alert('Link copied to clipboard!');
        }
        break;
      case 'library':
        setIsSaved(!isSaved);
        break;
      case 'next':
        alert(`${song.title} will play next!`);
        break;
      default:
        console.log(`${type} feature coming soon!`);
    }
  };

  const formatViews = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num;
  };

  return (
    <div
      className="group relative flex items-center justify-between p-2 md:p-3 rounded-xl bg-neutral-900/40 border border-neutral-800/50 hover:bg-neutral-800/60 hover:border-neutral-700 transition-all shadow-sm cursor-pointer"
      onClick={() => {
        // Play immediately inside the click handler (user-gesture unlock
        // for browser autoplay). Defer the route change so React doesn't
        // block paint on mounting the lazy SongPlayer route.
        playSong(song, playlist);
        startTransition(() => navigate(`/song/${song.id}`));
      }}
    >
      <div className="flex items-center gap-2 md:gap-3 min-w-0 pointer-events-none">
        <div className="relative shrink-0 w-10 h-10 md:w-12 md:h-12 rounded overflow-hidden">
          <img
            src={`${API_BASE}/songs/thumbnail/${song.youtubeId}`}
            alt={song.title}
            width="48"
            height="48"
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
          />
          <button 
            onClick={handlePlay}
            className="absolute inset-0 bg-black/40 md:opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity pointer-events-auto"
            aria-label={`Play ${song.title}`}
          >
            <Play className="w-4 h-4 md:w-5 md:h-5 text-white fill-current" />
          </button>
        </div>
        <div className="min-w-0">
          <h3 className="text-[13px] md:text-sm font-semibold truncate text-white group-hover:text-yellow-500 transition-colors">
            {song.title}
          </h3>
          <p className="text-[11px] md:text-xs text-neutral-400 truncate mt-0.5 md:mt-1">
            {song.artist?.name || 'Artist'} • {formatViews(song.views)} plays
          </p>
        </div>
      </div>
      
      <div className="relative" ref={menuRef}>
        <button 
          onClick={() => setShowMenu(!showMenu)}
          className={`p-1.5 md:p-2 rounded-full hover:bg-white/10 transition-all ${showMenu ? 'text-white' : 'text-neutral-500 md:opacity-0 group-hover:opacity-100'}`}
          aria-label={`More options for ${song.title}`}
        >
          <MoreVertical className="w-4 h-4 md:w-5 md:h-5" />
        </button>

        {showMenu && (
          <div className="absolute right-0 top-10 w-72 bg-neutral-900 border border-neutral-800 rounded-2xl shadow-2xl z-[100] overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Header */}
            <div className="p-4 border-b border-neutral-800 flex items-center justify-between bg-neutral-900/80 backdrop-blur-md">
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-white truncate">{song.title}</h3>
                <p className="text-xs text-neutral-400 truncate">{song.artist?.name || 'Artist'}</p>
              </div>
              <button onClick={() => setShowMenu(false)} className="text-neutral-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content Options */}
            <div className="p-2 py-3 overflow-y-auto max-h-[450px]">
              {/* Quick Actions Grid */}
              <div className="grid grid-cols-3 gap-2 mb-4 px-2">
                <button onClick={() => handleAction('next')} className="flex flex-col items-center gap-2 p-2 hover:bg-white/5 rounded-xl transition-colors">
                  <div className="p-2 bg-neutral-800 rounded-lg"><Play className="w-4 h-4 text-white" /></div>
                  <span className="text-[10px] text-neutral-400">Play next</span>
                </button>
                <button onClick={() => handleAction('playlist')} className="flex flex-col items-center gap-2 p-2 hover:bg-white/5 rounded-xl transition-colors">
                  <div className="p-2 bg-neutral-800 rounded-lg"><PlusSquare className="w-4 h-4 text-white" /></div>
                  <span className="text-[10px] text-neutral-400">Playlist</span>
                </button>
                <button onClick={() => handleAction('share')} className="flex flex-col items-center gap-2 p-2 hover:bg-white/5 rounded-xl transition-colors">
                  <div className="p-2 bg-neutral-800 rounded-lg"><Share2 className="w-4 h-4 text-white" /></div>
                  <span className="text-[10px] text-neutral-400">Share</span>
                </button>
              </div>

              {/* List Actions */}
              <div className="space-y-1">
                <MenuItem icon={Radio} text="Start mix" onClick={() => handleAction('mix')} />
                <MenuItem icon={ListMusic} text="Add to queue" onClick={() => handleAction('queue')} />
                <MenuItem 
                  icon={Library} 
                  text={isSaved ? "Saved to library" : "Save to library"} 
                  onClick={() => handleAction('library')}
                  active={isSaved}
                />
                <MenuItem icon={Album} text="Go to album" onClick={() => handleAction('album')} />
                <MenuItem icon={User} text="Go to artist" onClick={() => handleAction('artist')} />
                <MenuItem icon={Info} text="View song credits" onClick={() => handleAction('credits')} />
                <MenuItem icon={ThumbsDown} text="Not interested" onClick={() => handleAction('hate')} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default React.memo(SongListRow, (prev, next) => {
  return prev.song?.id === next.song?.id && prev.playlist === next.playlist;
});

function MenuItem({ icon: Icon, text, onClick, active }) {
  return (
    <button 
      onClick={onClick}
      className={`w-full flex items-center gap-4 px-4 py-2.5 text-sm rounded-lg transition-colors group/item ${active ? 'bg-yellow-500/10 text-yellow-500' : 'text-neutral-300 hover:bg-white/5'}`}
    >
      <Icon className={`w-5 h-5 transition-colors ${active ? 'text-yellow-500 fill-current' : 'text-neutral-500 group-hover/item:text-white'}`} />
      <span>{text}</span>
    </button>
  );
}
